/**
 * Form state for the Edward Jones Quick Plan (`edj-minimal-v1`).
 *
 * Seven fields: first and last name, date of birth, sex, ZIP, Married / Not married and net worth.
 * There is no smoking question and no partner, because the EDJ model input has neither.
 *
 * The rules and messages are the shared EDJ ones (`edjQuickPlan.ts` in @shared) that the server's
 * `validateShortFormIntakeRequest` also applies: a date of birth from 1900 to today, a ZIP that
 * resolves to a census region, Married / Not married, and a net worth from $0 to $1B. So the submit
 * button only enables for a request the API will accept. Name rules are the standard Quick Plan's.
 */
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  EDJ_QUICK_PLAN_MARITAL_CATEGORIES,
  EDJ_QUICK_PLAN_MESSAGES,
  EDJ_QUICK_PLAN_NET_WORTH_MAX,
  EDJ_QUICK_PLAN_NET_WORTH_MIN,
  edjQuickPlanBirthDateProblem,
  namedCensusRegionForZipCode,
} from '@shared';
import { quickPlanIdentitySchema } from './use-quick-plan-form';

export const EDJ_MARITAL_STATUS_OPTIONS = EDJ_QUICK_PLAN_MARITAL_CATEGORIES.map(value => ({
  value,
  label: value,
}));

export const edjQuickPlanSchema = quickPlanIdentitySchema
  .pick({ firstName: true, lastName: true })
  .extend({
    birthDate: quickPlanIdentitySchema.shape.birthDate.superRefine((birthDate, ctx) => {
      const problem = edjQuickPlanBirthDateProblem(birthDate);
      if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
    }),
    sex: z.enum(['male', 'female'], { required_error: 'Please select a sex' }),
    zipCode: z
      .string()
      .min(1, 'Zip code is required')
      .regex(/^\d{5}$/, 'Must be a 5-digit zip code')
      .refine(zipCode => namedCensusRegionForZipCode(zipCode) !== null, {
        message: EDJ_QUICK_PLAN_MESSAGES.zipCode,
      }),
    maritalStatus: z.enum(EDJ_QUICK_PLAN_MARITAL_CATEGORIES, {
      required_error: 'Please select a marital status',
    }),
    netWorth: z
      .number({ required_error: 'Net worth is required', invalid_type_error: 'Net worth is required' })
      .min(EDJ_QUICK_PLAN_NET_WORTH_MIN, EDJ_QUICK_PLAN_MESSAGES.netWorth)
      .max(EDJ_QUICK_PLAN_NET_WORTH_MAX, EDJ_QUICK_PLAN_MESSAGES.netWorth),
  });

export type EdjQuickPlanFormValues = z.infer<typeof edjQuickPlanSchema>;

interface UseEdjQuickPlanFormOptions {
  onSubmit: (values: EdjQuickPlanFormValues) => void;
  /** Names carried in from elsewhere, such as a CRM contact. */
  defaultValues?: Partial<EdjQuickPlanFormValues>;
}

export function useEdjQuickPlanForm({ onSubmit, defaultValues }: UseEdjQuickPlanFormOptions) {
  const form = useForm<EdjQuickPlanFormValues>({
    resolver: zodResolver(edjQuickPlanSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      zipCode: '',
      ...defaultValues,
    },
  });

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isValid: form.formState.isValid,
  };
}
